import React, { useEffect, useState, useRef } from "react";
import {
    ShoppingBag,
    Info,
    Loader2,
    Play,
    Video,
    RefreshCw,
    X,
    Maximize2,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { IVideo } from "../../../types/Response/CustomerResponse";
import { useLazyGetVideosQuery } from "../../../redux/actions/Customer/serviceCustomer";
import {
    useCreateCartMutation,
} from "../../../redux/actions/Customer/cartCustomer";

const ReelsVideosScreen: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const isPet = location.state?.isPet ?? false;

    const [page, setPage] = useState<number>(1);
    const [pageSize] = useState<number>(100);
    const [hasMore, setHasMore] = useState<boolean>(true);

    const [videosList, setVideosList] = useState<IVideo[]>([]);
    const [loadingCartId, setLoadingCartId] = useState<number | null>(null);

    const [selectedVideoModal, setSelectedVideoModal] =
        useState<IVideo | null>(null);

    const [notification, setNotification] = useState<{
        type: "success" | "error";
        message: string;
    } | null>(null);

    const notificationTimerRef =
        useRef<ReturnType<typeof setTimeout> | null>(null);

    const showNotification = (
        type: "success" | "error",
        message: string
    ) => {
        if (notificationTimerRef.current) {
            clearTimeout(notificationTimerRef.current);
        }

        setNotification({
            type,
            message,
        });

        notificationTimerRef.current = setTimeout(() => {
            setNotification(null);
        }, 3000);
    };

    useEffect(() => {
        return () => {
            if (notificationTimerRef.current) {
                clearTimeout(notificationTimerRef.current);
            }
        };
    }, []);

    const [triggerGetVideos, { isLoading, isFetching }] =
        useLazyGetVideosQuery();

    const [createCart] = useCreateCartMutation();

    // ============================================================
    // LOCK BODY SCROLL WHEN MODAL IS OPEN
    // ============================================================

    useEffect(() => {
        if (selectedVideoModal) {
            const originalOverflow = document.body.style.overflow;

            document.body.style.overflow = "hidden";

            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [selectedVideoModal]);

    // ============================================================
    // FETCH VIDEOS
    // ============================================================

    const fetchVideos = async (pageNumber: number) => {
        try {
            const response = await triggerGetVideos({
                page: pageNumber,
                pageSize,
                type: isPet ? 2 : 1,
            }).unwrap();

            if (response && response.items) {
                setHasMore(Boolean(response.hasMore));

                setVideosList((prev) =>
                    pageNumber === 1
                        ? response.items
                        : [...prev, ...response.items]
                );
            }
        } catch (error) {
            console.error(
                "Failed to fetch video catalog:",
                error
            );
        }
    };

    // ============================================================
    // RESET PAGINATION WHEN PRODUCT/PET MODE CHANGES
    // ============================================================

    useEffect(() => {
        setPage(1);
        setHasMore(true);
        setVideosList([]);
    }, [isPet]);

    useEffect(() => {
        fetchVideos(page);
    }, [page, isPet]);

    // ============================================================
    // LOAD NEXT PAGE ON SCROLL
    // ============================================================

    useEffect(() => {
        const handleScroll = () => {
            if (isFetching || !hasMore) return;

            const scrollPosition =
                window.innerHeight + window.scrollY;

            const threshold =
                document.documentElement.scrollHeight - 500;

            if (scrollPosition >= threshold) {
                setPage((prevPage) => prevPage + 1);
            }
        };

        window.addEventListener(
            "scroll",
            handleScroll,
            { passive: true }
        );

        return () => {
            window.removeEventListener(
                "scroll",
                handleScroll
            );
        };
    }, [isFetching, hasMore]);

    // ============================================================
    // ADD PRODUCT TO CART
    // ============================================================

    const handleAddToCartOrBooking = async (
        item: IVideo
    ) => {
        setLoadingCartId(item.id);

        try {
            if (item.type === "Product") {
                await createCart({
                    productId: Number(item.id),
                    quantity: 1,
                }).unwrap();

                showNotification(
                    "success",
                    "Product added to cart successfully!"
                );
            } else if (item.type === "Pet") {
                navigate(
                    `/productpage/PetDetailScreen/${item.id}`
                );
            }
        } catch (err: any) {
            const errorMessage =
                err?.data?.Message ||
                err?.data?.message ||
                "Unable to add item to cart";

            showNotification(
                "error",
                errorMessage
            );
        } finally {
            setLoadingCartId(null);
        }
    };

    // ============================================================
    // VIEW DETAILS
    // ============================================================

    const handleViewDetails = (
        item: IVideo
    ) => {
        if (item.type === "Product") {
            navigate(
                `/productpage/customerneproductpage/${item.id}`
            );
        } else {
            navigate(
                `/productpage/PetDetailScreen/${item.id}`
            );
        }
    };

    // ============================================================
    // CLOSE MODAL
    // ============================================================

    const closeVideoModal = () => {
        setSelectedVideoModal(null);
    };

    // ============================================================
    // ESCAPE KEY
    // ============================================================

    useEffect(() => {
        const handleEscape = (
            event: KeyboardEvent
        ) => {
            if (event.key === "Escape") {
                closeVideoModal();
            }
        };

        if (selectedVideoModal) {
            document.addEventListener(
                "keydown",
                handleEscape
            );
        }

        return () => {
            document.removeEventListener(
                "keydown",
                handleEscape
            );
        };
    }, [selectedVideoModal]);

    // ============================================================
    // MODAL VIDEO NAVIGATION
    // ============================================================

    const currentVideoIndex =
        selectedVideoModal
            ? videosList.findIndex(
                (video) =>
                    video.id ===
                    selectedVideoModal.id
            )
            : -1;

    const hasPreviousVideo =
        currentVideoIndex > 0;

    const hasNextVideo =
        currentVideoIndex >= 0 &&
        currentVideoIndex <
            videosList.length - 1;

    const handlePreviousVideo = () => {
        if (!hasPreviousVideo) return;

        setSelectedVideoModal(
            videosList[currentVideoIndex - 1]
        );
    };

    const handleNextVideo = () => {
        if (!hasNextVideo) return;

        setSelectedVideoModal(
            videosList[currentVideoIndex + 1]
        );
    };

    // ============================================================
    // KEYBOARD ARROW NAVIGATION
    // ============================================================

    useEffect(() => {
        const handleArrowNavigation = (
            event: KeyboardEvent
        ) => {
            if (!selectedVideoModal) return;

            if (event.key === "ArrowLeft") {
                event.preventDefault();
                handlePreviousVideo();
            } else if (
                event.key === "ArrowRight"
            ) {
                event.preventDefault();
                handleNextVideo();
            }
        };

        document.addEventListener(
            "keydown",
            handleArrowNavigation
        );

        return () => {
            document.removeEventListener(
                "keydown",
                handleArrowNavigation
            );
        };
    }, [
        selectedVideoModal,
        currentVideoIndex,
        videosList.length,
    ]);

    return (
        <>
            {/* ========================================================
                NOTIFICATION
            ========================================================= */}

            {notification && (
                <div
                    className={`
                        fixed
                        top-4
                        right-4
                        z-[10000]
                        px-4
                        py-3
                        rounded-xl
                        shadow-lg
                        border
                        text-xs
                        font-bold
                        flex
                        items-center
                        gap-2
                        max-w-[calc(100vw-2rem)]
                        transition-all
                        ${
                            notification.type ===
                            "success"
                                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                                : "bg-red-50 border-red-200 text-red-800"
                        }
                    `}
                >
                    <AlertCircle
                        size={14}
                        className="shrink-0"
                    />

                    <span className="break-words">
                        {notification.message}
                    </span>
                </div>
            )}

            {/* ========================================================
                MAIN
            ========================================================= */}

            <div
                className="
                    min-h-screen
                    w-full
                    bg-slate-50/50
                    text-slate-800
                    px-3
                    py-4
                    sm:px-5
                    sm:py-6
                    lg:px-8
                    lg:py-8
                "
            >
                <div className="w-full max-w-[1600px] mx-auto">

                    {/* ========================================================
                        HEADER
                    ========================================================= */}

                    <div
                        className="
                            flex
                            flex-col
                            sm:flex-row
                            sm:items-center
                            sm:justify-between
                            gap-4
                            border-b
                            border-slate-200
                            pb-5
                            mb-6
                        "
                    >
                        <div className="min-w-0">
                            <h1
                                className="
                                    text-xl
                                    sm:text-2xl
                                    font-bold
                                    text-slate-900
                                    flex
                                    items-center
                                    gap-2
                                "
                            >
                                <Video
                                    className="text-[#2F5E2E] shrink-0"
                                    size={24}
                                />

                                <span className="truncate">
                                    {isPet
                                        ? "Pet Video Gallery"
                                        : "Product Video Gallery"}
                                </span>
                            </h1>

                            <p className="text-xs sm:text-sm text-slate-500 mt-1">
                                {isPet
                                    ? "Browse promotional pet videos and manage pet linkages."
                                    : "Browse promotional product videos, monitor inventory status, and manage product linkages."}
                            </p>
                        </div>

                        <button
                            onClick={() => {
                                setVideosList([]);
                                setPage(1);
                                setHasMore(true);
                                fetchVideos(1);
                            }}
                            disabled={isFetching}
                            className="
                                w-full
                                sm:w-auto
                                shrink-0
                                inline-flex
                                items-center
                                justify-center
                                gap-2
                                bg-white
                                border
                                border-slate-200
                                hover:bg-slate-50
                                text-slate-700
                                px-4
                                py-2.5
                                rounded-xl
                                text-xs
                                font-semibold
                                shadow-xs
                                transition
                                cursor-pointer
                                disabled:opacity-50
                                disabled:cursor-not-allowed
                            "
                        >
                            <RefreshCw
                                size={14}
                                className={
                                    isFetching
                                        ? "animate-spin"
                                        : ""
                                }
                            />

                            <span>
                                Refresh Feed
                            </span>
                        </button>
                    </div>

                    {/* ========================================================
                        CONTENT
                    ========================================================= */}

                    {isLoading &&
                    videosList.length === 0 ? (
                        <div
                            className="
                                py-24
                                text-center
                                text-slate-500
                                flex
                                flex-col
                                items-center
                                justify-center
                            "
                        >
                            <Loader2
                                className="animate-spin text-[#2F5E2E] mb-2"
                                size={28}
                            />

                            <span className="text-xs font-semibold">
                                Loading video streams...
                            </span>
                        </div>
                    ) : videosList.length ===
                      0 ? (
                        <div
                            className="
                                bg-white
                                border
                                border-dashed
                                border-slate-200
                                rounded-2xl
                                p-10
                                sm:p-16
                                text-center
                                text-slate-400
                                text-sm
                            "
                        >
                            No videos found in the
                            system catalog.
                        </div>
                    ) : (
                        <div
                            className="
                                grid
                                grid-cols-1
                                min-[480px]:grid-cols-2
                                lg:grid-cols-3
                                2xl:grid-cols-4
                                gap-4
                                sm:gap-5
                                lg:gap-6
                            "
                        >
                            {videosList.map(
                                (video) => (
                                    <VideoCardItem
                                        key={
                                            video.id
                                        }
                                        video={video}
                                        onOpenModal={(
                                            v
                                        ) =>
                                            setSelectedVideoModal(
                                                v
                                            )
                                        }
                                        onAddToCart={
                                            handleAddToCartOrBooking
                                        }
                                        onViewDetails={
                                            handleViewDetails
                                        }
                                        isLoadingCart={
                                            loadingCartId ===
                                            video.id
                                        }
                                    />
                                )
                            )}
                        </div>
                    )}
                </div>

                {/* ============================================================
                    RESPONSIVE VIDEO MODAL
                ============================================================ */}

                {selectedVideoModal && (
                    <div
                        className="
                            fixed
                            inset-0
                            z-[9999]
                            w-screen
                            h-[100dvh]
                            bg-black/80
                            backdrop-blur-sm
                            flex
                            items-center
                            justify-center
                            p-2
                            sm:p-4
                            lg:p-6
                            overflow-hidden
                        "
                        onClick={closeVideoModal}
                    >
                        <div
                            className="
                                relative
                                w-full
                                max-w-[1500px]
                                h-[calc(100dvh-1rem)]
                                sm:h-[calc(100dvh-2rem)]
                                lg:h-[calc(100dvh-3rem)]
                                max-h-[1000px]
                                bg-black
                                rounded-xl
                                sm:rounded-2xl
                                lg:rounded-3xl
                                overflow-hidden
                                shadow-2xl
                                border
                                border-white/10
                                flex
                                flex-col
                                min-h-0
                            "
                            onClick={(e) =>
                                e.stopPropagation()
                            }
                        >
                            {/* CLOSE */}

                            <button
                                onClick={
                                    closeVideoModal
                                }
                                className="
                                    absolute
                                    top-2
                                    right-2
                                    sm:top-4
                                    sm:right-4
                                    z-50
                                    w-9
                                    h-9
                                    sm:w-10
                                    sm:h-10
                                    rounded-full
                                    bg-black/70
                                    hover:bg-black
                                    backdrop-blur-md
                                    text-white
                                    flex
                                    items-center
                                    justify-center
                                    border
                                    border-white/20
                                    shadow-lg
                                    transition
                                    cursor-pointer
                                "
                                title="Close"
                            >
                                <X size={18} />
                            </button>

                            {/* PREVIOUS */}

                            <button
                                onClick={
                                    handlePreviousVideo
                                }
                                disabled={
                                    !hasPreviousVideo
                                }
                                className={`
                                    absolute
                                    left-2
                                    sm:left-4
                                    lg:left-6
                                    top-1/2
                                    -translate-y-1/2
                                    z-50
                                    w-9
                                    h-9
                                    sm:w-11
                                    sm:h-11
                                    rounded-full
                                    flex
                                    items-center
                                    justify-center
                                    border
                                    border-white/20
                                    backdrop-blur-md
                                    shadow-lg
                                    transition
                                    ${
                                        hasPreviousVideo
                                            ? "bg-black/70 hover:bg-black text-white cursor-pointer"
                                            : "bg-black/30 text-white/30 cursor-not-allowed"
                                    }
                                `}
                                title="Previous video"
                            >
                                <ChevronLeft
                                    size={20}
                                />
                            </button>

                            {/* NEXT */}

                            <button
                                onClick={
                                    handleNextVideo
                                }
                                disabled={
                                    !hasNextVideo
                                }
                                className={`
                                    absolute
                                    right-2
                                    sm:right-4
                                    lg:right-6
                                    top-1/2
                                    -translate-y-1/2
                                    z-50
                                    w-9
                                    h-9
                                    sm:w-11
                                    sm:h-11
                                    rounded-full
                                    flex
                                    items-center
                                    justify-center
                                    border
                                    border-white/20
                                    backdrop-blur-md
                                    shadow-lg
                                    transition
                                    ${
                                        hasNextVideo
                                            ? "bg-black/70 hover:bg-black text-white cursor-pointer"
                                            : "bg-black/30 text-white/30 cursor-not-allowed"
                                    }
                                `}
                                title="Next video"
                            >
                                <ChevronRight
                                    size={20}
                                />
                            </button>

                            {/* VIDEO */}

                            <div
                                className="
                                    relative
                                    flex-1
                                    min-h-0
                                    min-w-0
                                    w-full
                                    bg-black
                                    flex
                                    items-center
                                    justify-center
                                    overflow-hidden
                                "
                            >
                                <video
                                    key={
                                        selectedVideoModal.id
                                    }
                                    src={
                                        selectedVideoModal.videoUrl
                                    }
                                    poster={
                                        selectedVideoModal.imageUrl ||
                                        selectedVideoModal.thumbnailUrl
                                    }
                                    controls
                                    autoPlay
                                    playsInline
                                    className="
                                        block
                                        w-full
                                        h-full
                                        max-w-full
                                        max-h-full
                                        object-contain
                                    "
                                />
                            </div>

                            {/* DETAILS */}

                            <div
                                className="
                                    shrink-0
                                    w-full
                                    bg-white
                                    text-slate-900
                                    px-4
                                    py-3
                                    sm:px-5
                                    sm:py-4
                                    lg:px-6
                                    lg:py-4
                                    max-h-[120px]
                                    sm:max-h-[140px]
                                    lg:max-h-[150px]
                                    overflow-y-auto
                                "
                            >
                                <div
                                    className="
                                        flex
                                        items-center
                                        justify-between
                                        gap-3
                                    "
                                >
                                    <span
                                        className="
                                            shrink-0
                                            text-[9px]
                                            sm:text-[10px]
                                            font-bold
                                            px-2
                                            py-1
                                            rounded-md
                                            bg-emerald-100
                                            text-emerald-800
                                            uppercase
                                            tracking-wider
                                        "
                                    >
                                        {
                                            selectedVideoModal.type
                                        }
                                    </span>

                                    {/* HIDE PRICE FOR ADOPT PET */}

                                    {!(
                                        selectedVideoModal.type ===
                                            "Pet" &&
                                        selectedVideoModal.petSellType?.toLowerCase() ===
                                            "adopt"
                                    ) && (
                                        <span
                                            className="
                                                shrink-0
                                                text-sm
                                                sm:text-base
                                                font-black
                                                text-[#2F5E2E]
                                                whitespace-nowrap
                                            "
                                        >
                                            AED{" "}
                                            {Number(
                                                selectedVideoModal?.salePrice ??
                                                    0
                                            ) > 0
                                                ? selectedVideoModal.salePrice
                                                : selectedVideoModal.price}
                                        </span>
                                    )}
                                </div>

                                <div className="flex flex-row items-center justify-between gap-3">
                                    <h3
                                        className="
                                            mt-2
                                            text-sm
                                            sm:text-base
                                            lg:text-lg
                                            font-bold
                                            leading-snug
                                            break-words
                                            line-clamp-2
                                            flex-1
                                        "
                                    >
                                        {
                                            selectedVideoModal.name
                                        }
                                    </h3>

                                    {/* MODAL ACTION */}

                                    <button
                                        onClick={() => {
                                            if (
                                                selectedVideoModal.type ===
                                                "Product"
                                            ) {
                                                handleAddToCartOrBooking(
                                                    selectedVideoModal
                                                );
                                            } else {
                                                handleViewDetails(
                                                    selectedVideoModal
                                                );
                                            }
                                        }}
                                        disabled={
                                            selectedVideoModal.type ===
                                                "Product" &&
                                            !selectedVideoModal.inStock
                                        }
                                        className={`
                                            shrink-0
                                            flex
                                            items-center
                                            justify-center
                                            gap-1.5
                                            py-2.5
                                            px-2
                                            sm:px-3
                                            rounded-xl
                                            text-[11px]
                                            sm:text-xs
                                            font-bold
                                            transition

                                            ${
                                                selectedVideoModal.type ===
                                                "Product"
                                                    ? !selectedVideoModal.inStock
                                                        ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                                        : "bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white cursor-pointer"
                                                    : "bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white cursor-pointer"
                                            }
                                        `}
                                    >
                                        {selectedVideoModal.type ===
                                        "Product" ? (
                                            <>
                                                <ShoppingBag
                                                    size={14}
                                                    className="shrink-0"
                                                />

                                                <span className="truncate">
                                                    Add to
                                                    Cart
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <Info
                                                    size={14}
                                                    className="shrink-0"
                                                />

                                                <span>
                                                    View
                                                </span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

// ========================================================================
// VIDEO CARD
// ========================================================================

interface VideoCardItemProps {
    video: IVideo;
    onOpenModal: (v: IVideo) => void;
    onAddToCart: (v: IVideo) => void;
    onViewDetails: (v: IVideo) => void;
    isLoadingCart: boolean;
}

const VideoCardItem: React.FC<
    VideoCardItemProps
> = ({
    video,
    onOpenModal,
    onAddToCart,
    onViewDetails,
    isLoadingCart,
}) => {
    const videoRef =
        useRef<HTMLVideoElement>(null);

    const [isHovered, setIsHovered] =
        useState(false);

    const handleMouseEnter = () => {
        setIsHovered(true);

        if (videoRef.current) {
            videoRef.current.currentTime = 0;

            videoRef.current
                .play()
                .catch(() => {});
        }
    };

    const handleMouseLeave = () => {
        setIsHovered(false);

        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
    };

    // ============================================================
    // PRICE
    // ============================================================

    const currentPrice =
        Number(video?.salePrice ?? 0) > 0
            ? Number(video.salePrice)
            : Number(video.price ?? 0);

    const originalPrice =
        Number(video?.salePrice ?? 0) > 0 &&
        Number(video?.price ?? 0) >
            Number(video.salePrice ?? 0)
            ? Number(video.price)
            : null;

    // ============================================================
    // ADOPTION CHECK
    // ============================================================

    const isAdoptPet =
        video.type === "Pet" &&
        video.petSellType?.toLowerCase() ===
            "adopt";

    const isProductOutOfStock =
        !video.inStock;

    return (
        <div
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className="
                relative
                w-full
                overflow-hidden
                rounded-[20px]
                bg-slate-200
                shadow-sm
                transition-all
                duration-300
                hover:shadow-lg
                group
            "
        >
            {/* ========================================================
                VIDEO
            ========================================================= */}

            <div
                className="
                    relative
                    w-full
                    aspect-[9/16]
                    overflow-hidden
                    bg-slate-900
                "
            >
                {/* POSTER */}

                <img
                    src={
                        video.imageUrl ||
                        video.thumbnailUrl ||
                        "/placeholder-image.png"
                    }
                    alt={video.name}
                    className={`
                        absolute
                        inset-0
                        w-full
                        h-full
                        object-cover
                        transition-all
                        duration-500
                        ${
                            isHovered
                                ? "opacity-0 scale-[1.02]"
                                : "opacity-100 scale-100"
                        }
                    `}
                />

                {/* HOVER VIDEO */}

                <video
                    ref={videoRef}
                    src={video.videoUrl}
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className={`
                        absolute
                        inset-0
                        w-full
                        h-full
                        object-cover
                        transition-all
                        duration-500
                        ${
                            isHovered
                                ? "opacity-100 scale-100"
                                : "opacity-0 scale-[1.02]"
                        }
                    `}
                />

                {/* DARK GRADIENT */}

                <div
                    className="
                        absolute
                        inset-0
                        bg-gradient-to-b
                        from-black/20
                        via-transparent
                        to-black/55
                        pointer-events-none
                    "
                />

                {/* PET STATUS */}

                {video.type === "Pet" &&
                    video.petStatus && (
                        <div
                            className="
                                absolute
                                top-12
                                left-3
                                z-20
                                px-2.5
                                py-1
                                rounded-lg
                                bg-black/70
                                backdrop-blur-md
                                border
                                border-white/20
                                text-white
                                text-[9px]
                                sm:text-[10px]
                                font-black
                                uppercase
                                tracking-wide
                                shadow-md
                            "
                        >
                            {video.petStatus}
                        </div>
                    )}

                {/* MAXIMIZE */}

                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenModal(video);
                    }}
                    className="
                        absolute
                        top-3
                        right-3
                        z-30
                        w-8
                        h-8
                        sm:w-9
                        sm:h-9
                        rounded-full
                        bg-black/60
                        hover:bg-black/80
                        backdrop-blur-md
                        text-white
                        flex
                        items-center
                        justify-center
                        border
                        border-white/20
                        shadow-md
                        transition-all
                        duration-200
                        cursor-pointer
                    "
                    title="Expand Video"
                >
                    <Maximize2 size={14} />
                </button>

                {/* PLAY */}

                {!isHovered && (
                    <div
                        className="
                            absolute
                            inset-0
                            z-20
                            flex
                            items-center
                            justify-center
                            pointer-events-none
                        "
                    >
                        <div
                            className="
                                w-11
                                h-11
                                sm:w-12
                                sm:h-12
                                rounded-full
                                bg-white/85
                                backdrop-blur-sm
                                flex
                                items-center
                                justify-center
                                shadow-xl
                            "
                        >
                            <Play
                                size={19}
                                className="
                                    fill-slate-800
                                    text-slate-800
                                    ml-0.5
                                "
                            />
                        </div>
                    </div>
                )}

                {/* =====================================================
                    FLOATING PRODUCT / PET INFO
                ====================================================== */}

                <div
                    className="
                        absolute
                        left-2
                        right-2
                        bottom-2
                        sm:left-3
                        sm:right-3
                        sm:bottom-3
                        z-30
                    "
                >
                    <div
                        className="
                            w-full
                            bg-white
                            rounded-[18px]
                            px-2.5
                            py-2.5
                            sm:px-3
                            sm:py-2.5
                            shadow-xl
                            flex
                            items-center
                            gap-2.5
                        "
                    >
                        {/* THUMBNAIL */}

                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewDetails(video);
                            }}
                            className="
                                shrink-0
                                w-10
                                h-10
                                sm:w-11
                                sm:h-11
                                rounded-xl
                                overflow-hidden
                                border
                                border-slate-200
                                bg-slate-100
                                cursor-pointer
                            "
                        >
                            <img
                                src={
                                    video.imageUrl ||
                                    video.thumbnailUrl ||
                                    "/placeholder-image.png"
                                }
                                alt={video.name}
                                className="
                                    w-full
                                    h-full
                                    object-cover
                                "
                            />
                        </button>

                        {/* NAME + PRICE */}

                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewDetails(video);
                            }}
                            className="
                                flex-1
                                min-w-0
                                text-left
                                cursor-pointer
                            "
                        >
                            <div
                                className="
                                    text-[11px]
                                    sm:text-xs
                                    font-bold
                                    text-slate-800
                                    truncate
                                "
                                title={video.name}
                            >
                                {video.name}
                            </div>

                            {/* PRICE */}

                            {!isAdoptPet && (
                                <div
                                    className="
                                        flex
                                        items-center
                                        gap-1.5
                                        mt-0.5
                                        flex-wrap
                                    "
                                >
                                    {currentPrice >
                                        0 && (
                                        <span
                                            className="
                                                text-[12px]
                                                sm:text-sm
                                                font-extrabold
                                                text-[#2F6B32]
                                                whitespace-nowrap
                                            "
                                        >
                                            AED{" "}
                                            {currentPrice.toFixed(
                                                2
                                            )}
                                        </span>
                                    )}

                                    {originalPrice !==
                                        null && (
                                        <span
                                            className="
                                                text-[10px]
                                                sm:text-[11px]
                                                text-slate-400
                                                line-through
                                                whitespace-nowrap
                                            "
                                        >
                                            AED{" "}
                                            {originalPrice.toFixed(
                                                2
                                            )}
                                        </span>
                                    )}
                                </div>
                            )}
                        </button>

                        {/* =================================================
                            ACTION BUTTON
                        ================================================= */}

                        {video.type ===
                        "Product" ? (
                            // PRODUCT -> ADD TO CART
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();

                                    if (
                                        isProductOutOfStock
                                    ) {
                                        return;
                                    }

                                    onAddToCart(video);
                                }}
                                disabled={
                                    isProductOutOfStock ||
                                    isLoadingCart
                                }
                                className={`
                                    shrink-0
                                    w-10
                                    h-10
                                    sm:w-11
                                    sm:h-11
                                    rounded-full
                                    flex
                                    items-center
                                    justify-center
                                    transition-all
                                    duration-200

                                    ${
                                        isProductOutOfStock
                                            ? `
                                                bg-slate-200
                                                text-slate-400
                                                cursor-not-allowed
                                            `
                                            : `
                                                bg-[#2F6B32]
                                                hover:bg-[#245527]
                                                active:scale-95
                                                text-white
                                                cursor-pointer
                                            `
                                    }
                                `}
                                title={
                                    video.inStock
                                        ? "Add to Cart"
                                        : "Sold Out"
                                }
                            >
                                {isLoadingCart ? (
                                    <Loader2
                                        size={18}
                                        className="animate-spin"
                                    />
                                ) : (
                                    <span className="text-2xl font-light leading-none">
                +
            </span>
                                )}
                            </button>
                        ) : (
                            // PET -> VIEW
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onViewDetails(
                                        video
                                    );
                                }}
                                className="
                                    shrink-0
                                    inline-flex
                                    items-center
                                    justify-center
                                    gap-1.5
                                    px-3
                                    sm:px-4
                                    h-10
                                    sm:h-11
                                    rounded-xl
                                    bg-[#2F6B32]
                                    hover:bg-[#245527]
                                    active:scale-95
                                    text-white
                                    text-[11px]
                                    sm:text-xs
                                    font-bold
                                    transition-all
                                    duration-200
                                    cursor-pointer
                                "
                                title="View Pet"
                            >
                             
                                <span>
                                    View
                                </span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReelsVideosScreen;